import { Box, Card, Chip, Grid, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import { Area, Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useDashboard } from '../../StateManagement/dashboardContext';

function DashboardChart() {
    const { charts } = useDashboard();

    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const COLORS = ['#4AD06E', '#08B1B6', '#FFBB28', '#FF8042'];

    const transformedLoanSanctionData = charts.monthlyLoanSanction.map(item => ({
        ...item,
        label: `${monthNames[item._id.month - 1]} ${item._id.year}`,
    }));

    const transformedRepaymentData = charts.monthlyRepayments.map((item) => ({
        ...item,
        label: `${monthNames[item._id.month - 1]} ${item._id.year}`
    }));


    return (
        <Grid p={3}>
            <Grid container spacing={3} direction="column" p={3}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        {charts.monthlyLoanSanction.length > 0 && (
                            <Card
                                sx={{
                                    p: 3,
                                    borderRadius: 3,
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                    background: 'linear-gradient(to bottom, #ffffff, #f8fafc)',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 12px 28px rgba(0,0,0,0.15)'
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                                    <Box>
                                        <Typography variant="h5" fontWeight={700} gutterBottom color="primary.main">
                                            Monthly Loan Sanctions
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Track loan approval trends over time
                                        </Typography>
                                    </Box>
                                    <Chip
                                        label="Live Data"
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                        icon={<TrendingUpIcon fontSize="small" />}
                                    />
                                </Box>

                                <ResponsiveContainer width="100%" height={220}>
                                    <LineChart
                                        data={transformedLoanSanctionData}
                                        margin={{ top: 5, right: 20, left: 10, bottom: 15 }}
                                    >
                                        <defs>
                                            <linearGradient id="loanGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#3f51b5" stopOpacity={0.8} />
                                                <stop offset="95%" stopColor="#3f51b5" stopOpacity={0.1} />
                                            </linearGradient>
                                        </defs>

                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            vertical={false}
                                            stroke="#e2e8f0"
                                        />

                                        <XAxis
                                            dataKey="label"
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fontSize: 12, fill: '#64748b' }}
                                            tickMargin={10}
                                        />

                                        <YAxis
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fontSize: 12, fill: '#64748b' }}
                                            tickFormatter={(value) => value.toLocaleString()}
                                            width={60}
                                        />

                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: 'white',
                                                borderRadius: 8,
                                                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                                border: '1px solid #e2e8f0'
                                            }}
                                            labelStyle={{
                                                color: '#1e293b',
                                                fontWeight: 600,
                                                marginBottom: 8,
                                                fontSize: 13
                                            }}
                                            itemStyle={{
                                                color: '#64748b',
                                                fontSize: 13
                                            }}
                                            formatter={(value) => [`${value} loans`, 'Count']}
                                        />

                                        <Line
                                            type="monotone"
                                            dataKey="count"
                                            name="Loans Sanctioned"
                                            stroke="#3f51b5"
                                            strokeWidth={3}
                                            dot={{
                                                fill: '#3f51b5',
                                                strokeWidth: 2,
                                                r: 5,
                                                stroke: '#fff'
                                            }}
                                            activeDot={{
                                                r: 8,
                                                fill: '#3f51b5',
                                                stroke: '#fff',
                                                strokeWidth: 2
                                            }}
                                        />

                                        {/* Area under the line for better visual impact */}
                                        <Area
                                            type="monotone"
                                            dataKey="count"
                                            fill="url(#loanGradient)"
                                            stroke="transparent"
                                            opacity={0.3}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>

                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    alignItems: 'center',
                                    mt: 2,
                                    pt: 2,
                                    borderTop: '1px dashed',
                                    borderColor: 'divider'
                                }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                                        <InfoOutlinedIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                        Updated {new Date().toLocaleDateString()}
                                    </Typography>
                                </Box>
                            </Card>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        {charts.userRoles.length > 0 && (
                            <Card sx={{ p: 3, borderRadius: 1, boxShadow: 1 }}>
                                <Typography variant="h6" fontWeight={600} color="text.primary">
                                    User Roles Movement
                                </Typography>
                                <ResponsiveContainer width="100%" height={320}>
                                    <PieChart>
                                        <Pie
                                            data={charts.userRoles}
                                            dataKey="count"
                                            nameKey="_id"
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={100}
                                            label={({ percent, name }) =>
                                                `${name} (${(percent * 100).toFixed(0)}%)`
                                            }
                                        >
                                            {charts.userRoles.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Card>
                        )}
                    </Grid>
                </Grid>

                <Grid item xs={12} md={6}>
                    {charts.monthlyRepayments.length > 0 && <Card sx={{ p: 3, borderRadius: 1, boxShadow: 1 }}>
                        <Typography variant="h6" fontWeight={600} mb={2} color="text.primary">
                            Monthly Repayments
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={transformedRepaymentData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="label" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="principal" stackId="a" fill="#3f51b5" name="Principal" />
                                <Bar dataKey="interest" stackId="a" fill="#f50057" name="Interest" />
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>}
                </Grid>

                {/* <Grid item xs={12} md={6}>
                    <ResponsiveContainer>
                        <LineChart data={charts.interestTrendData}>
                            <XAxis dataKey="label" />
                            <YAxis />
                            <Tooltip />
                            <Line type="monotone" dataKey="totalInterest" stroke="#7091E6" />
                        </LineChart>
                    </ResponsiveContainer>
                </Grid> */}
            </Grid>
        </Grid>
    )
}

export default DashboardChart